"use client";

import { useEffect, useMemo, useState } from "react";
import DiscussionDrawer from "@/components/panels/DiscussionDrawer";
import { CHRONICLE_LOCATION_KIND_OPTIONS, toDateSafe } from "@/lib/chronicles";

function toInputDate(value) {
  const date = toDateSafe(value);
  if (!date) return "";
  const iso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  return iso.slice(0, 10);
}

function fromInputDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export default function ChronicleIncidentDrawer({
  open,
  mode = "create",
  incident = null,
  existingLocationEntities = [],
  onClose,
  onSubmit,
  canPublish = true,
  saving = false,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [primaryLocationMode, setPrimaryLocationMode] = useState("none");
  const [primaryLocationEntityId, setPrimaryLocationEntityId] = useState("");
  const [primaryLocationName, setPrimaryLocationName] = useState("");
  const [primaryLocationKind, setPrimaryLocationKind] = useState("other");
  const [primaryLocationAddress, setPrimaryLocationAddress] = useState("");
  const [primaryLocationCity, setPrimaryLocationCity] = useState("");
  const [primaryLocationState, setPrimaryLocationState] = useState("");
  const [primaryLocationCountry, setPrimaryLocationCountry] = useState("");
  const [primaryLocationPlaceId, setPrimaryLocationPlaceId] = useState("");
  const [primaryLocationLatitude, setPrimaryLocationLatitude] = useState("");
  const [primaryLocationLongitude, setPrimaryLocationLongitude] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [approximate, setApproximate] = useState(false);
  const [status, setStatus] = useState("draft");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle(incident?.title || "");
    setDescription(incident?.description || "");
    const existingPrimary = incident?.primaryLocationEntityId || "";
    const existingEntity = existingLocationEntities.find((item) => item.id === existingPrimary);
    setPrimaryLocationEntityId(existingPrimary);
    if (existingPrimary && existingEntity) {
      setPrimaryLocationMode("existing");
      setPrimaryLocationName(existingEntity.displayName || "");
      setPrimaryLocationKind(existingEntity?.typed?.locationKind || "other");
      setPrimaryLocationAddress(existingEntity?.typed?.formattedAddress || "");
      setPrimaryLocationCity(existingEntity?.typed?.city || "");
      setPrimaryLocationState(existingEntity?.typed?.state || "");
      setPrimaryLocationCountry(existingEntity?.typed?.country || "");
      setPrimaryLocationPlaceId(existingEntity?.typed?.placeId || "");
      setPrimaryLocationLatitude(
        existingEntity?.typed?.latitude === null || existingEntity?.typed?.latitude === undefined
          ? ""
          : String(existingEntity.typed.latitude)
      );
      setPrimaryLocationLongitude(
        existingEntity?.typed?.longitude === null || existingEntity?.typed?.longitude === undefined
          ? ""
          : String(existingEntity.typed.longitude)
      );
    } else if (incident?.location) {
      setPrimaryLocationMode("create");
      setPrimaryLocationName(incident.location);
      setPrimaryLocationKind("other");
      setPrimaryLocationAddress(incident.location);
      setPrimaryLocationCity("");
      setPrimaryLocationState("");
      setPrimaryLocationCountry("");
      setPrimaryLocationPlaceId("");
      setPrimaryLocationLatitude("");
      setPrimaryLocationLongitude("");
    } else {
      setPrimaryLocationMode("none");
      setPrimaryLocationName("");
      setPrimaryLocationKind("other");
      setPrimaryLocationAddress("");
      setPrimaryLocationCity("");
      setPrimaryLocationState("");
      setPrimaryLocationCountry("");
      setPrimaryLocationPlaceId("");
      setPrimaryLocationLatitude("");
      setPrimaryLocationLongitude("");
    }
    setStartAt(toInputDate(incident?.temporal?.startAt || incident?.sortKey || null));
    setEndAt(toInputDate(incident?.temporal?.endAt || null));
    setApproximate(Boolean(incident?.temporal?.approximate));
    setStatus(incident?.status === "published" ? "published" : "draft");
    setError("");
  }, [open, incident?.id, incident?.location, incident?.primaryLocationEntityId, existingLocationEntities]);

  const submitLabel = useMemo(() => {
    if (saving) return mode === "edit" ? "Saving…" : "Creating…";
    return mode === "edit" ? "Save changes" : "Create incident";
  }, [mode, saving]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanTitle = title.trim();
    const cleanBody = description.trim();
    const startDate = fromInputDate(startAt);
    const endDate = fromInputDate(endAt);
    if (!cleanTitle) {
      setError("Title is required.");
      return;
    }
    if (!cleanBody) {
      setError("Description is required.");
      return;
    }
    if (!startDate) {
      setError("Start date is required.");
      return;
    }
    if (endDate && endDate.getTime() < startDate.getTime()) {
      setError("End date cannot be before start date.");
      return;
    }
    setError("");
    await onSubmit?.({
      title: cleanTitle,
      description: cleanBody,
      status: canPublish ? status : "draft",
      temporal: {
        startAt: startDate,
        endAt: endDate,
        approximate,
      },
      sortKey: startDate,
      primaryLocation:
        primaryLocationMode === "none"
          ? { mode: "none" }
          : primaryLocationMode === "existing"
          ? { mode: "existing", entityId: primaryLocationEntityId }
          : {
              mode: "create",
              entity: {
                displayName: primaryLocationName.trim(),
                aliases: [],
                summary: "",
                typed: {
                  locationKind: primaryLocationKind,
                  formattedAddress: primaryLocationAddress.trim(),
                  city: primaryLocationCity.trim(),
                  state: primaryLocationState.trim(),
                  country: primaryLocationCountry.trim(),
                  latitude: primaryLocationLatitude === "" ? null : Number(primaryLocationLatitude),
                  longitude: primaryLocationLongitude === "" ? null : Number(primaryLocationLongitude),
                  placeId: primaryLocationPlaceId.trim() || null,
                },
              },
            },
    });
  };

  return (
    <DiscussionDrawer open={open} onClose={onClose} title={mode === "edit" ? "Edit Incident" : "New Incident"}>
      <div className="container-app h-full overflow-y-auto pb-6">
        <form className="space-y-4 pt-4" onSubmit={handleSubmit}>
          <div className="space-y-1">
            <label className="block text-sm font-medium">Title</label>
            <input className="input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} required />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium">Description</label>
            <textarea
              className="input min-h-[140px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={6}
              maxLength={4000}
              required
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium">Start date</label>
              <input className="input" type="date" value={startAt} onChange={(event) => setStartAt(event.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium">End date (optional)</label>
              <input className="input" type="date" value={endAt} onChange={(event) => setEndAt(event.target.value)} />
            </div>
          </div>

          <div className="space-y-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3">
            <div>
              <label className="block text-sm font-medium">Primary location (optional)</label>
              <p className="mt-1 text-xs text-[var(--muted)]">
                Select an existing location entity or create a new one. Additional locations can be linked from the incident detail page.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-[var(--fg-primary)]">
                <input
                  type="radio"
                  name="primary-location-mode"
                  checked={primaryLocationMode === "none"}
                  onChange={() => setPrimaryLocationMode("none")}
                />
                None
              </label>
              {existingLocationEntities.length > 0 ? (
                <label className="inline-flex items-center gap-2 text-sm text-[var(--fg-primary)]">
                  <input
                    type="radio"
                    name="primary-location-mode"
                    checked={primaryLocationMode === "existing"}
                    onChange={() => setPrimaryLocationMode("existing")}
                  />
                  Select existing
                </label>
              ) : null}
              <label className="inline-flex items-center gap-2 text-sm text-[var(--fg-primary)]">
                <input
                  type="radio"
                  name="primary-location-mode"
                  checked={primaryLocationMode === "create"}
                  onChange={() => setPrimaryLocationMode("create")}
                />
                Create new
              </label>
            </div>

            {primaryLocationMode === "existing" ? (
              <label className="space-y-1">
                <span className="block text-sm font-medium">Existing location</span>
                <select
                  className="input"
                  value={primaryLocationEntityId}
                  onChange={(event) => setPrimaryLocationEntityId(event.target.value)}
                >
                  <option value="">Choose a location</option>
                  {existingLocationEntities.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.displayName}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {primaryLocationMode === "create" ? (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Location name</span>
                    <input className="input" value={primaryLocationName} onChange={(event) => setPrimaryLocationName(event.target.value)} maxLength={140} />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Location type</span>
                    <select className="input" value={primaryLocationKind} onChange={(event) => setPrimaryLocationKind(event.target.value)}>
                      {CHRONICLE_LOCATION_KIND_OPTIONS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="space-y-1">
                  <span className="block text-sm font-medium">Formatted address</span>
                  <input className="input" value={primaryLocationAddress} onChange={(event) => setPrimaryLocationAddress(event.target.value)} maxLength={200} />
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">City</span>
                    <input className="input" value={primaryLocationCity} onChange={(event) => setPrimaryLocationCity(event.target.value)} maxLength={120} />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">State / region</span>
                    <input className="input" value={primaryLocationState} onChange={(event) => setPrimaryLocationState(event.target.value)} maxLength={120} />
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Country</span>
                    <input className="input" value={primaryLocationCountry} onChange={(event) => setPrimaryLocationCountry(event.target.value)} maxLength={120} />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Place ID</span>
                    <input className="input" value={primaryLocationPlaceId} onChange={(event) => setPrimaryLocationPlaceId(event.target.value)} maxLength={160} />
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Latitude</span>
                    <input className="input" value={primaryLocationLatitude} onChange={(event) => setPrimaryLocationLatitude(event.target.value)} inputMode="decimal" />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-sm font-medium">Longitude</span>
                    <input className="input" value={primaryLocationLongitude} onChange={(event) => setPrimaryLocationLongitude(event.target.value)} inputMode="decimal" />
                  </label>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-[var(--fg-primary)]">
              <input type="checkbox" checked={approximate} onChange={(event) => setApproximate(event.target.checked)} />
              Approximate date
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-[var(--fg-primary)]">
              <span>Status</span>
              <select
                className="input min-w-[120px]"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                disabled={!canPublish}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-[var(--radius)] bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[var(--brand-foreground)] disabled:opacity-60"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </DiscussionDrawer>
  );
}
